import React from "react";
import ReactDOM from "react-dom";
import { graphql } from "react-relay";

import { faBadgeCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { isEqual, omit } from "lodash";
import * as redom from "redom";
import { Image, type ImageProps } from "semantic-ui-react";

import ThemeContext from "~/common/ThemeContext";
import UserPopup from "~/common/UserPopup";
import { ReactComponent as ConnectedOutlinedDarkIcon } from "~/legacy-assets/images/icons/connected-outlined-dark.svg";
import { ReactComponent as ConnectedOutlinedIcon } from "~/legacy-assets/images/icons/connected-outlined.svg";
import * as dom from "~/lib/dom";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { getAnonymousUserIcon, setTooltip, type SetTooltipOptions, uiAvatar, type UIAvatarOptions } from "~/lib/utils";
import { SingleName } from "~/ui/avatar/NameList";

import type { AvatarUserQuery } from "~/__generated__/AvatarUserQuery.graphql";

import "~/common/legacy/UserList.global.module.css";
import styles from "~/common/legacy/UserList.global.module.css";

export interface UserAttrs {
	id: string;
	email?: string | null;
	image?: string | React.ImgHTMLAttributes<HTMLImageElement> | null;
	fullName?: string;
	firstName?: string;
	lastName?: string;
	title?: string | null;
	isActive?: boolean;
	isConfigured?: boolean;
	isGuest?: boolean;
	isAnonymous?: boolean;
	isTopUser?: boolean;
	isExternal?: boolean;
	invitable?: boolean;
}

export interface TeamAttrs {
	id: string;
	title: string;
	handle?: string;
	location?: string;
	description?: string;
	archived?: boolean;
}

interface LinkUserParams {
	user?: UserAttrs;
	link?: boolean;
	position?:
		| "bottom left"
		| "bottom center"
		| "top left"
		| "top right"
		| "bottom right"
		| "right center"
		| "left center"
		| "top center";
}

function linkUser({ user, link = true, position = "bottom left" }: LinkUserParams, children: React.ReactElement) {
	if (!link || !user) return children;

	return (
		<UserPopup key={user.id} as="a" position={position} user={user}>
			{children}
		</UserPopup>
	);
}

interface GuestUserParams {
	user?: UserAttrs;
	className?: string;
	style?: React.CSSProperties;
}

function guestUser({ user, className, style }: GuestUserParams, children: React.ReactElement) {
	if (!user) return children;
	const cleanedStyles = { ...style };
	delete cleanedStyles.fontSize;
	delete cleanedStyles.width;
	delete cleanedStyles.height;

	// Note: this sucks and I hate it. See comment in UserList.less. - C
	return (
		<span className={cx(styles.guestAvatar, className)} style={cleanedStyles}>
			{children}
		</span>
	);
}

function externalUser({ user, className, style }: GuestUserParams, children: React.ReactElement) {
	if (!user) return children;
	const cleanedStyles = { ...style };
	delete cleanedStyles.fontSize;
	delete cleanedStyles.width;
	delete cleanedStyles.height;

	return (
		<ThemeContext.Consumer>
			{({ isDarkMode }) => (
				<span className={cx(styles.externalUserAvatar, className)} style={cleanedStyles}>
					{children}
					{isDarkMode ? <ConnectedOutlinedDarkIcon /> : <ConnectedOutlinedIcon />}
				</span>
			)}
		</ThemeContext.Consumer>
	);
}

function topUser({ user, className, style }: GuestUserParams, children: React.ReactElement) {
	if (!user) return children;

	return (
		<span className={cx(styles.topUserAvatar, className)} style={style}>
			{children}
			<div className={styles.topUserBg} />
			<FontAwesomeIcon icon={faBadgeCheck} className={styles.topUserBadge} />
		</span>
	);
}

export type AvatarProps = ImageProps & {
	user?: UserAttrs;
	team?: TeamAttrs;
	circle?: boolean;
	invitable?: boolean;
	initials?: string | number;
	width?: string;
	tooltip?: boolean | string;
	tooltipWidth?: string;
	tooltipPosition?: string;
	withAlt?: boolean;
	style?: object;
	link?: boolean;
	uiAvatarOptions?: UIAvatarOptions;
	useTitle?: boolean;
	hideDiamond?: boolean;
	hideExternalIcon?: boolean;
	dashedOutline?: boolean;
};

interface AvatarState {
	fallback: string;
	source: string | React.ImgHTMLAttributes<HTMLImageElement> | undefined | null;
	originalSource: string | React.ImgHTMLAttributes<HTMLImageElement> | undefined | null;
	notFound: boolean;
}

// This will contain string lookups for urls that 404, so future renders don't need to retry 404'ed urls
const failedUrls = new Set<string>();

/** @deprecated Check out the new component at ~/ui/avatar! */
class Avatar extends React.Component<AvatarProps, AvatarState> {
	state: AvatarState = {
		fallback: "",
		source: "",
		originalSource: "",
		notFound: false,
	};

	shouldComponentUpdate(nextProps: AvatarProps, nextState: AvatarState): boolean {
		return !isEqual(this.state, nextState) || !isEqual(this.props, nextProps);
	}

	static getDerivedStateFromProps(props: AvatarProps, state: AvatarState): AvatarState {
		const { user, team, uiAvatarOptions } = props;

		let source = user ? user.image : null;
		if (source && typeof source === "object" && source.src) {
			source = source.src;
		}

		let { initials } = props;
		if (!initials) {
			if (user) {
				if (user.fullName) {
					initials = user.fullName;
				} else if (user.email) {
					initials = user.email;
				}
			} else if (team) {
				initials = team.title;
			}
		}

		let fallback = "";
		if (initials) {
			fallback = uiAvatar(team ? faUsers : initials, uiAvatarOptions);
			if (!source) source = fallback;
			else if (typeof source == "string" && failedUrls.has(source)) {
				// console.log("saved a futile attempt at retrieving source image");
				source = fallback;
			}
		}

		// Don't update state if on404 was called and the original source has not changed
		if (state.notFound && state.originalSource === source) return state;

		return {
			fallback,
			source,
			originalSource: source,
			notFound: false,
		};
	}

	on404 = () => {
		const { source, fallback, notFound } = this.state;
		if (notFound) return;

		if (source !== fallback) {
			typeof source == "string" && failedUrls.add(source);
			this.setState(({ fallback }) => ({ source: fallback, notFound: true }));
		}
	};

	render() {
		const {
			user,
			team,
			rounded,
			circle,
			invitable,
			width = "2",
			tooltip,
			tooltipWidth,
			tooltipPosition = "top center",
			link,
			hideDiamond,
			hideExternalIcon,
			style: styleProp,
			self: _self, // Don't pass `self` to <Image />
			uiAvatarOptions: _uiAvatarOptions, // Don't pass `uiAvatarOptions` to <Image />
			withAlt,
			dashedOutline,
			useTitle = true,
			...rest
		} = this.props;
		const widthScalar = parseFloat(width),
			widthUnit = width.substring(String(widthScalar).length) || "em";

		const style = {
			fontSize: `${widthScalar / 2}${widthUnit}`,
			...styleProp,
		};

		const className = cx("user-avatar", this.props.className, {
			rounded,
			circle,
			invitable,
			dashedOutline,
		});

		const avatarProps = {
			...rest,
			style,
			className,
		};

		let { initials } = this.props;
		if (!initials) {
			if (user) {
				if (user.fullName) {
					initials = user.fullName;
				} else if (user.email) {
					initials = user.email;
				}
			} else if (team) {
				initials = team.title;
			}
		}

		let alt = "";
		if (withAlt) alt = user ? `A picture of ${user.fullName}` : `A placeholder label for ${initials}`;

		let imageTitle = "";
		if (!tooltip)
			imageTitle = user
				? (user.fullName ?? `${initials}${typeof initials === "number" ? " more" : ""}`)
				: team
					? team.title
					: `${initials}`;
		let imageElement = (
			<Image
				key={user ? user.id : team ? team.id : initials}
				avatar={true}
				title={useTitle ? imageTitle : undefined}
				alt={alt}
				src={this.state.source}
				data-email={user && user.email}
				onError={this.on404}
				{...avatarProps}
			/>
		);

		if (link) {
			imageElement = linkUser(this.props, imageElement);
		}

		if (!hideDiamond) {
			// The external user icon takes precedence over the guest and top user icon.
			if (user?.isExternal && !hideExternalIcon) {
				imageElement = externalUser({ user, className, style }, imageElement);
			} else if (user?.isGuest) {
				imageElement = guestUser({ user, className, style }, imageElement);
			} else if (user?.isTopUser) {
				imageElement = topUser({ user, className, style }, imageElement);
			}
		}

		// Anonymous users will also be external if they come from a note stream invite
		// In that case we know who that invite came from and so we don't want to show them as anonymous
		if (user?.isAnonymous && !user?.isExternal) {
			imageElement = (
				<div {...avatarProps} className={cx(className, "ui avatar image anonymous-avatar")}>
					<FontAwesomeIcon icon={getAnonymousUserIcon(user?.fullName)} fixedWidth />
				</div>
			);
		}

		if (tooltip) {
			const tooltipProps = {
				className: "user-avatar-wrapper",
				...setTooltip(
					typeof tooltip === "string" ? tooltip : user?.fullName ? user.fullName : team ? team.title : "",
					tooltipPosition,
					undefined,
					undefined,
					tooltipWidth,
				),
			};

			return (
				<span {...tooltipProps} style={{ ...omit(avatarProps.style, "fontSize"), ...tooltipProps.style }}>
					{imageElement}
				</span>
			);
		} else {
			return imageElement;
		}
	}
}

export const avatarUserQuery = graphql`
	query AvatarUserQuery($userId: ID!) {
		node(id: $userId) {
			__typename
			... on User {
				id
				email
				image
				fullName
				firstName
				lastName
				title
				isActive
				isConfigured
				isGuest
				isTopUser
				isExternal
			}
		}
	}
`;

type LazyAvatarProps = AvatarProps & {
	userId: string;
} & { usernameContainerPromise?: Promise<HTMLDivElement> };

function LazyAvatarContent({ userId, usernameContainerPromise, ...rest }: LazyAvatarProps) {
	const { node: user } = useLazyLoadQuery<AvatarUserQuery>(
		avatarUserQuery,
		{ userId },
		{ fetchPolicy: "store-or-network" },
	);
	const [usernameContainer, setUsernameContainer] = React.useState<HTMLDivElement | undefined>();
	React.useEffect(() => {
		usernameContainerPromise?.then(v => {
			setUsernameContainer(v);
		});
	}, [usernameContainerPromise]);
	// FIXME(p7g): This case should probably be handled better
	if (user?.__typename !== "User") return null;
	return (
		<>
			<Avatar user={user} {...rest} />
			{usernameContainer && ReactDOM.createPortal(<SingleName user={user} />, usernameContainer)}
		</>
	);
}

export function LazyAvatar({
	preloader,
	...props
}: LazyAvatarProps & { preloader: React.ReactElement; usernameContainerPromise?: Promise<HTMLDivElement> }) {
	return (
		<React.Suspense fallback={preloader}>
			<LazyAvatarContent {...props} />
		</React.Suspense>
	);
}

export default Avatar;

export interface AvatarDOMComponentData {
	user: UserAttrs;
	width: string;
	dashedOutline?: boolean;
	isDarkMode: boolean;
	background?: string;
	color?: string;
	initials?: string | number;
}

// Implements, using the DOM API, a subset of the above component needed for
// the notes editor.
export class AvatarDOMComponent implements dom.DOMComponent<AvatarDOMComponentData> {
	el: HTMLElement;
	avatarImageElement: HTMLImageElement;

	fallback = "";
	source: string;
	originalSource: string;
	notFound = false;

	constructor(private data: AvatarDOMComponentData) {
		const { width, user, isDarkMode, background, color, initials: dataInitials } = data;
		let initials;

		if (dataInitials) {
			initials = dataInitials;
		}
		if (user.fullName) {
			initials = user.fullName;
		} else if (user.email) {
			initials = user.email;
		}

		if (initials) {
			this.fallback = uiAvatar(initials, { background, color });
		}
		this.source = (user.image && typeof user.image === "object" ? user.image.src : user.image) ?? this.fallback;
		this.originalSource = this.source;

		this.avatarImageElement = redom.el("img", {
			class: "ui avatar image user-avatar",
		});
		this.avatarImageElement.onerror = this.onError;
		this.avatarImageElement.src = this.source;
		this.avatarImageElement.title =
			user.fullName ?? `${initials?.toString()}${typeof initials === "number" ? " more" : ""}` ?? "";
		this.avatarImageElement.dataset.email = user.email ?? undefined;
		const widthScalar = parseFloat(width),
			widthUnit = width.substring(String(widthScalar).length) || "em";
		this.avatarImageElement.style.fontSize = `${widthScalar / 2}${widthUnit}`;
		if (this.data.dashedOutline) {
			this.avatarImageElement.classList.add("dashedOutline");
			this.setTooltip("This user does not have access to this note", {
				width: "140px",
				position: "bottom right",
			});
		} else {
			this.avatarImageElement.classList.remove("dashedOutline");
			this.removeTooltip();
		}

		this.el = redom.el("span", [this.avatarImageElement]);

		if (user?.isGuest) {
			this.el.classList.add(styles.guestAvatar);
		} else {
			this.el.classList.remove(styles.guestAvatar);
			if (user?.isExternal) {
				this.el.classList.add(styles.externalUserAvatar);
				this.el.appendChild(dom.render(isDarkMode ? <ConnectedOutlinedDarkIcon /> : <ConnectedOutlinedIcon />));
			}
		}
	}

	update() {
		if (this.source !== this.avatarImageElement.src) {
			this.avatarImageElement.src = this.source;
		}
	}

	private onError = () => {
		if (this.notFound) return;

		if (this.source !== this.fallback) {
			failedUrls.add(this.source);
			this.source = this.fallback;
			this.update();
		}
	};

	setTooltip = (text: string, options?: Partial<SetTooltipOptions>) => {
		const { style, ...props } = setTooltip(text, options);
		Object.entries(props).forEach(([k, v]) => {
			this.avatarImageElement.setAttribute(k, String(v));
		});
		if (style) {
			Object.entries(style).forEach(([k, v]) => {
				this.avatarImageElement.style.setProperty(k, v);
			});
		}
	};

	removeTooltip() {
		// This just removes all the props instead of reset them to original values,
		// but it should be fine for now
		const { style: _style, ...props } = setTooltip("");
		Object.keys(props).forEach(k => {
			this.avatarImageElement.removeAttribute(k);
		});
	}
}
